import { useEffect, useRef, useState } from 'react';
import { ApiError, api, type CustomerListItem, type Me, type WsCekmece, type WsOnTalepCekmecesi, type WsDestekCekmecesi, type WsKisi, type WsOlay, type WsTur } from './api';
import { mailtoKur, yanitTaslagi } from './destek-yanit';
import { WS } from './metin/web-sitesi';
import { WebOrderDetail } from './Commerce';
import { KisiSeridi } from './WebSitesiGrafik';
import { bultenEtiketi } from './WebSitesiListeler';
import { Hata, Olcekli, useVeri } from './WebSitesiOrtak';
import { gunBae, tarihSaatBae } from './web-sitesi-bicim';

/**
 * Kişi sayfası ve çekmece (taslak A, 07.10.2026): aynı zaman çizelgesi iki yerde. Çekmece bir
 * satırın ayrıntısı, kişi sayfası o kişinin bütün geçmişi ve CRM kaydıyla eşleştirme kararı.
 */

function ZamanCizelgesi({ olaylar, secili, setSecili, gosterilecek }: { olaylar: WsOlay[]; secili: string; setSecili: (t: string) => void; gosterilecek?: boolean }) {
  const tipler = ['hepsi', ...new Set(olaylar.map((e) => e.type))];
  const liste = olaylar.filter((e) => secili === 'hepsi' || e.type === secili);
  return (
    <>
      {gosterilecek === false ? null : (
        <div className="filters" style={{ marginBottom: 'var(--space-sm)' }}>
          {tipler.map((t) => (
            <button key={t} type="button" className="chip" aria-pressed={secili === t} data-testid={`ws-olay-${t}`} onClick={() => setSecili(t)}>
              {t === 'hepsi' ? WS.olayTumu : WS.olay[t as keyof typeof WS.olay]}
              <b>{t === 'hepsi' ? olaylar.length : olaylar.filter((e) => e.type === t).length}</b>
            </button>
          ))}
        </div>
      )}
      <ol className="tl" data-testid="ws-zaman">
        {liste.map((e, i) => (
          <li key={`${e.type}-${e.at}-${i}`} className={e.tone}>
            <div className="t">{e.at === null ? WS.tarihYok : `${tarihSaatBae(e.at)} ${WS.baeSaati}`}</div>
            <div className="d">
              <span className="kind">{WS.olay[e.type]}</span>
              {e.text}
            </div>
          </li>
        ))}
      </ol>
    </>
  );
}

// ---------------------------------------------------------------------------
// CRM müşteri kaydı kararı
// ---------------------------------------------------------------------------

function KararKarti({ k, me, yenile }: { k: WsKisi; me: Me; yenile: () => void }) {
  const kutu = useRef<HTMLElement | null>(null);
  const baglayabilir = me.permissions.includes('web_intake:link');
  const [acik, setAcik] = useState(false);
  const [aranan, setAranan] = useState('');
  const [sonuc, setSonuc] = useState<CustomerListItem[] | null>(null);
  const [not, setNot] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const m = k.member;

  useEffect(() => {
    if (aranan.trim().length < 3) {
      setSonuc(null);
      return undefined;
    }
    let iptal = false;
    const t = setTimeout(() => {
      api.customers({ q: aranan.trim(), pageSize: 6 }).then((r) => { if (!iptal) setSonuc(r.items); }, () => { if (!iptal) setSonuc([]); });
    }, 250);
    return () => { iptal = true; clearTimeout(t); };
  }, [aranan]);

  const calistir = async (islem: () => Promise<unknown>) => {
    if (!not.trim()) {
      setHata(WS.kisi.notEtiket);
      return;
    }
    setMesgul(true);
    setHata(null);
    try {
      await islem();
      yenile();
    } catch (e) {
      setHata(e instanceof ApiError ? e.message : WS.hata);
    } finally {
      setMesgul(false);
    }
  };

  return (
    <section className="card" id="decide" ref={kutu} data-testid="ws-karar">
      <h3>{WS.kisi.crmKart}</h3>
      {m.master ? (
        <>
          <p className="sub">{WS.kisi.crmBagliAlt}</p>
          <p style={{ marginTop: 'var(--space-sm)' }}><span className="tag ok">{WS.kisi.crmBagli(m.master.name ?? '')}</span></p>
        </>
      ) : (
        <p className="sub">{m.reviewState === 'RESOLVED' ? WS.kisi.crmAyri : WS.kisi.crmKartAlt}</p>
      )}
      {baglayabilir ? (
        <div className="decide" style={{ marginTop: 'var(--space-md)' }}>
          {!m.master ? (
            <>
              <p><b>{WS.kisi.sorun}</b></p>
              <p style={{ color: 'var(--ink-soft)' }}>{WS.kisi.sorunNot}</p>
            </>
          ) : null}
          <label>
            <span className="cnote" style={{ margin: 0 }}>{WS.kisi.notEtiket}</span>
            <input type="text" value={not} data-testid="ws-karar-not" onChange={(e) => setNot(e.target.value)} style={{ width: '100%' }} />
          </label>
          <div className="row">
            {m.master ? (
              <button type="button" className="btn" disabled={mesgul} data-testid="ws-karar-kaldir" onClick={() => void calistir(() => api.resolveWebCustomerReview(m.externalId, { note: not.trim(), unlink: true }))}>
                {WS.kisi.bagiKaldir}
              </button>
            ) : (
              <>
                <button type="button" className="btn primary" disabled={mesgul} data-testid="ws-karar-bagla-ac" onClick={() => setAcik((a) => !a)}>
                  {WS.kisi.baglaDugme}
                </button>
                <button type="button" className="btn" disabled={mesgul} data-testid="ws-karar-ayri" onClick={() => void calistir(() => api.resolveWebCustomerReview(m.externalId, { note: not.trim() }))}>
                  {WS.kisi.ayriTut}
                </button>
              </>
            )}
          </div>
          {acik && !m.master ? (
            <div data-testid="ws-karar-kutu">
              <input type="search" placeholder={WS.kisi.musteriAra} aria-label={WS.kisi.musteriAraAria} value={aranan} data-testid="ws-karar-ara" onChange={(e) => setAranan(e.target.value)} style={{ width: '100%' }} />
              <p className="cnote" style={{ margin: 0 }}>{aranan.trim().length < 3 ? WS.kisi.uc : sonuc && sonuc.length === 0 ? WS.kisi.sonucYok : ''}</p>
              {sonuc?.map((c) => (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 6 }} data-testid="ws-karar-sonuc">
                  <span>{c.displayName}<span className="cnote" style={{ margin: 0 }}> {c.email ?? c.phone ?? ''}</span></span>
                  <button type="button" className="btn sm primary" disabled={mesgul} onClick={() => void calistir(() => api.linkWebCustomer(m.externalId, { customerId: c.id, note: not.trim() }))}>
                    {WS.kisi.baglaSec}
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          {hata ? <p role="alert" style={{ color: 'var(--danger)' }} data-testid="ws-karar-hata">{hata}</p> : null}
        </div>
      ) : (
        <p className="cnote">{WS.kisi.yetkiYok}</p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Kişi sayfası
// ---------------------------------------------------------------------------

export function KisiSayfasi({ externalId, me, git, kararaGit }: { externalId: string; me: Me; git: (h: string) => void; kararaGit: boolean }) {
  const r = useVeri<WsKisi>(() => api.webPanelMember(externalId), [externalId]);
  const [pf, setPf] = useState('hepsi');
  const k = r.veri;
  useEffect(() => {
    if (!kararaGit || !k) return;
    const t = setTimeout(() => document.getElementById('decide')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
    return () => clearTimeout(t);
  }, [kararaGit, k]);
  if (!k) return r.hata ? <Hata mesaj={r.hata} yenile={r.yenile} /> : <p>{WS.yukleniyor}</p>;
  const m = k.member;
  const sipSay = k.orders;
  const bulten = bultenEtiketi(m.newsletter);
  const simdi = new Date(k.measuredAt);
  const crmEtiket = m.master ? { sinif: 'tag ok', metin: WS.kisi.crmBagli(m.master.name ?? '') } : m.reviewState === 'RESOLVED' ? { sinif: 'tag mute', metin: WS.kisi.crmAyri } : { sinif: 'tag warn', metin: WS.kisi.crmYok };
  return (
    <div data-testid="ws-kisi">
      <div className="crumb">
        <button type="button" onClick={() => git('tab:uyeler')} data-testid="ws-kisi-geri">{WS.kisi.geri}</button>
        <span>{WS.kisi.yol(m.name)}</span>
      </div>
      <section className="card">
        <div className="phead">
          <div className="avatar" aria-hidden="true">{[...m.name][0] ?? ''}</div>
          <div>
            <h2>{m.name}</h2>
            <div className="chips">
              {m.emailVerifiedAt ? <span className="tag ok">{WS.kisi.dogrulandi}</span> : <span className="tag mute">{WS.kisi.dogrulanmadi}</span>}
              <span className={bulten.sinif}>{m.newsletter === 'CONFIRMED' ? WS.kisi.bultenOnayli : m.newsletter === null ? WS.kisi.bultenYok : bulten.metin}</span>
              <span className={crmEtiket.sinif}>{crmEtiket.metin}</span>
              {m.sessionCount === 0 && !m.lastLoginAt ? <span className="tag bad">{WS.kisi.hicOturum}</span> : null}
            </div>
          </div>
        </div>
        <div className="pstats">
          <div className="ps"><b>{sipSay.count}</b><span>{WS.kisi.siparisSay}</span></div>
          <div className="ps"><b>{sipSay.paid}</b><span>{WS.kisi.odenen}</span></div>
          <div className="ps"><b>{sipSay.pending}</b><span>{WS.kisi.bekliyorIptal(sipSay.cancelled)}</span></div>
          <div className="ps"><b>{m.sessionCount ?? '-'}</b><span>{WS.kisi.oturum}</span></div>
        </div>
        <dl className="pfacts">
          <div><dt>{WS.kisi.uyelikTarihi}</dt><dd>{gunBae(m.registeredAt)}</dd></div>
          <div><dt>{WS.kisi.ilkOturum}</dt><dd>{m.firstLoginAt ? gunBae(m.firstLoginAt) : m.sessionsKnown ? WS.kisi.yok : WS.kisi.aktarilmadi}</dd></div>
          <div><dt>{WS.kisi.sonGorulme}</dt><dd>{m.daysSinceSeen !== null ? WS.gunOnce(Math.round(m.daysSinceSeen)) : m.sessionsKnown ? WS.uyeler.hicOturum : WS.kisi.aktarilmadi}</dd></div>
          <div><dt>{WS.kisi.telefon}</dt><dd>{m.phone ?? WS.kisi.yok}</dd></div>
          <div><dt>{WS.kisi.uyelikOnayi}</dt><dd>{m.membershipConsentedAt ? gunBae(m.membershipConsentedAt) : WS.kisi.kaydedilmemis}</dd></div>
          <div><dt>{WS.kisi.kademe}</dt><dd>{m.tier ?? WS.kisi.aktarilmadi}</dd></div>
        </dl>
      </section>
      <div className="pgrid">
        <div style={{ display: 'grid', gap: 'var(--space-lg)', minWidth: 0 }}>
          <section className="card" id="v-pstrip" data-testid="ws-kisi-serit">
            <div className="card-head">
              <div>
                <h3>{WS.kisi.son40}</h3>
                <p className="sub">{WS.kisi.son40Alt}</p>
              </div>
            </div>
            <Olcekli>{(w) => <KisiSeridi genislik={w} ad={m.name} olaylar={k.events} simdi={simdi} />}</Olcekli>
            <div className="legend">
              <span><svg width="14" height="14"><circle cx="7" cy="7" r="5" fill="var(--accent)" /></svg>{WS.kisi.lejandUyelik}</span>
              <span><svg width="14" height="14"><circle cx="7" cy="7" r="5" fill="var(--mute)" /></svg>{WS.kisi.lejandIptal}</span>
              <span><svg width="14" height="14"><circle cx="7" cy="7" r="5" fill="var(--warn)" /></svg>{WS.kisi.lejandBekleyen}</span>
            </div>
          </section>
          <section className="card">
            <div className="card-head">
              <div>
                <h3>{WS.kisi.zaman}</h3>
                <p className="sub">{WS.kisi.zamanAlt}</p>
              </div>
              {sipSay.count > 0 ? <button type="button" className="btn sm" data-testid="ws-kisi-siparisler" onClick={() => git(`pord:${m.externalId}`)}>{WS.kisi.siparislerdeAc(sipSay.count)}</button> : null}
            </div>
            {k.events.some((e) => e.type === 'destek') ? null : <p className="cnote" style={{ margin: '0 0 var(--space-sm)' }}>{WS.kisi.destekYok}</p>}
            <ZamanCizelgesi olaylar={k.events} secili={pf} setSecili={setPf} />
          </section>
        </div>
        <div className="side">
          <KararKarti k={k} me={me} yenile={r.yenile} />
          <section className="card">
            <h3>{WS.kisi.hukuk}</h3>
            <p className="sub">{WS.kisi.hukukMetin}</p>
          </section>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Destek mesajı: kaynak, sınıf ve yanıt (17.10.2026)
// ---------------------------------------------------------------------------

function DestekKaynagi({ s, git, siparisiAc }: { s: WsDestekCekmecesi; git: (h: string) => void; siparisiAc: () => void }) {
  const D = WS.destek;
  const elle = s.spamMark === 'SPAM' ? D.elleSpam : s.spamMark === 'DEGIL' ? D.elleDegil : null;
  return (
    <section data-testid="ws-destek-kaynak">
      <div className="dr-sec"><h4>{D.kaynakBaslik}</h4></div>
      <dl className="facts">
        <div><dt>{D.nereden}</dt><dd data-testid="ws-destek-akis">{D.akis[s.akis]}</dd></div>
        <div><dt>{D.kategoriEtiket}</dt><dd data-testid="ws-destek-kategori">{s.kategori ? D.kategori[s.kategori] : D.serbest}</dd></div>
        <div><dt>{D.dilEtiket}</dt><dd title={D.dilNot}>{D.dil[s.dil]}</dd></div>
        <div><dt>{D.gonderen}</dt><dd data-testid="ws-destek-eposta">{s.email}</dd></div>
        <div>
          <dt>{D.siparisEtiket}</dt>
          <dd>
            {s.orderReference ? (
              <button type="button" className="link-btn" data-testid="ws-destek-siparis" onClick={siparisiAc}>{s.orderReference}</button>
            ) : (
              D.siparisYok
            )}
          </dd>
        </div>
        <div><dt>{D.musteriEtiket}</dt><dd data-testid="ws-destek-musteri">{s.customer ? s.customer.name : D.musteriDegil}</dd></div>
        <div>
          <dt>{D.uyeEtiket}</dt>
          <dd data-testid="ws-destek-uye">
            {s.member ? (
              <button type="button" className="link-btn" onClick={() => git(`person:${s.member!.externalId}`)}>{s.member.name}</button>
            ) : (
              D.uyeDegil
            )}
          </dd>
        </div>
      </dl>
      {s.system ? (
        <p className="cnote" data-testid="ws-destek-sistem">{D.sistemMesaji}</p>
      ) : (
        <>
          <p>
            <span className="cnote" style={{ margin: 0 }}>{D.sinifEtiket} </span>
            <span className={s.sinif === 'SPAM' ? 'tag bad' : s.sinif === 'SUPHELI' ? 'tag warn' : 'tag ok'} data-testid="ws-destek-sinif">{D.sinif[s.sinif]}</span>
          </p>
          {elle ? <p className="cnote" data-testid="ws-destek-elle">{elle}</p> : null}
          <p className="cnote" style={{ margin: 0 }}>{D.nedenBaslik} ({D.puan(s.puan)})</p>
          {s.kurallar.length === 0 ? (
            <p className="cnote" data-testid="ws-destek-neden">{D.nedenYok}</p>
          ) : (
            <ul data-testid="ws-destek-neden">
              {s.kurallar.map((k) => (
                <li key={k.kod}>{k.neden} ({k.puan > 0 ? `+${k.puan}` : k.puan})</li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function OnTalepIletisim({ o }: { o: WsOnTalepCekmecesi }) {
  const D = WS.onTalep;
  const [mesaj, setMesaj] = useState<string | null>(null);
  const kopyala = async (metin: string) => {
    try {
      await navigator.clipboard.writeText(metin);
      setMesaj(D.kopyalandi);
    } catch {
      setMesaj(D.kopyalanamadi);
    }
  };
  const kisi = o.adults === null && o.children === null && o.infants === null
    ? (o.partySize === null ? null : D.kisiSayisi(o.partySize))
    : [D.yetiskin(o.adults ?? 0), ...((o.children ?? 0) > 0 ? [D.cocuk(o.children ?? 0)] : []), ...((o.infants ?? 0) > 0 ? [D.bebek(o.infants ?? 0)] : [])].join(', ');
  const teklifSatirlari: [string, string][] = [
    ...(kisi === null ? [] : [[D.kisi, kisi] as [string, string]]),
    ...(o.travelOn ? [[D.tarih, gunBae(o.travelOn)] as [string, string]] : []),
    ...(o.pricedTotalMinor !== null && o.pricedCurrency !== null ? [[D.toplam, `${(o.pricedTotalMinor / 100).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${o.pricedCurrency}`] as [string, string]] : []),
  ];
  return (
    <section data-testid="ws-ontalep">
      <div className="dr-sec"><h4>{D.baslik}</h4></div>
      {o.phone ? (
        <>
          <p data-testid="ws-ontalep-telefon"><b>{D.telefon}:</b> {o.phone}</p>
          <div className="row">
            {o.waRakamlar ? <a className="btn primary" href={`https://wa.me/${o.waRakamlar}`} target="_blank" rel="noopener noreferrer" data-testid="ws-ontalep-wa">{D.whatsappYaz}</a> : null}
            {o.telNumara ? <a className="btn" href={`tel:${o.telNumara}`} data-testid="ws-ontalep-ara">{D.ara}</a> : null}
            <button type="button" className="btn" data-testid="ws-ontalep-tel-kopya" onClick={() => void kopyala(o.phone ?? '')}>{D.telKopyala}</button>
          </div>
          {o.waRakamlar ? null : <p className="cnote" data-testid="ws-ontalep-bicimsiz">{D.numaraBicimsiz}</p>}
        </>
      ) : o.iletisimYok && o.whatsappTeklif ? (
        <>
          <p className="cnote" data-testid="ws-ontalep-yok">{o.aramaKodu ? D.yokNot : D.yokNotKodsuz}</p>
          {o.aramaKodu ? (
            <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--space-sm)', alignItems: 'center' }}>
              <code data-testid="ws-ontalep-kod" style={{ overflowWrap: 'anywhere' }}>{o.aramaKodu}</code>
              <button type="button" className="btn" data-testid="ws-ontalep-kod-kopya" onClick={() => void kopyala(o.aramaKodu ?? '')}>{D.kodKopyala}</button>
            </div>
          ) : null}
        </>
      ) : null}
      {mesaj ? <p role="status" data-testid="ws-ontalep-mesaj">{mesaj}</p> : null}
      {o.whatsappTeklif ? (
        <>
          <dl className="facts" data-testid="ws-ontalep-teklif">
            {teklifSatirlari.map(([e, v]) => (<div key={e}><dt>{e}</dt><dd>{v}</dd></div>))}
          </dl>
          <p className="cnote">{D.karar}</p>
        </>
      ) : null}
    </section>
  );
}

function DestekYaniti({ s, who, reference }: { s: WsDestekCekmecesi; who: string; reference: string }) {
  const D = WS.destek.yanit;
  const [mesaj, setMesaj] = useState<string | null>(null);
  const taslak = yanitTaslagi({ email: s.email, subject: s.subject, reference, name: who, dil: s.dil });
  const [konu, setKonu] = useState(taslak.subject);
  const [metin, setMetin] = useState(taslak.body);
  // Çekmece başka kayda geçince taslak (referans konuda ve metinde geçtiği için) değişir ve yeniden yüklenir;
  // aynı kayıtta yazılan, durum güncellemesinden sonraki yenilemede korunur (taslak aynı kalır).
  useEffect(() => {
    setKonu(taslak.subject);
    setMetin(taslak.body);
    setMesaj(null);
  }, [reference, taslak.subject, taslak.body]);
  const mailto = mailtoKur(s.email, konu, metin);
  const kopyala = async (deger: string) => {
    try {
      await navigator.clipboard.writeText(deger);
      setMesaj(D.kopyalandi);
    } catch {
      setMesaj(D.kopyalanamadi);
    }
  };
  const taslagaDon = () => {
    setKonu(taslak.subject);
    setMetin(taslak.body);
    setMesaj(D.taslagaDondu);
  };
  return (
    <section data-testid="ws-destek-yanit">
      <div className="dr-sec"><h4>{D.baslik}</h4></div>
      <p className="cnote">{D.alt}</p>
      <div className="row">
        {mailto.href ? (
          <a className="btn primary" href={mailto.href} data-testid="ws-yanit-mailto">{D.epostaAc}</a>
        ) : (
          <p role="alert" data-testid="ws-yanit-gecersiz">{D.adresGecersiz}</p>
        )}
        <button type="button" className="btn" data-testid="ws-yanit-kopya-adres" onClick={() => void kopyala(s.email)}>{D.adresKopyala}</button>
        <button type="button" className="btn" data-testid="ws-yanit-kopya-konu" onClick={() => void kopyala(konu)}>{D.konuKopyala}</button>
        <button type="button" className="btn" data-testid="ws-yanit-kopya-metin" onClick={() => void kopyala(metin)}>{D.metinKopyala}</button>
        <button type="button" className="btn" data-testid="ws-yanit-taslaga-don" onClick={taslagaDon}>{D.taslagaDon}</button>
      </div>
      {mailto.uzun ? <p className="cnote" role="note" data-testid="ws-yanit-uzun">{D.uzunNot}</p> : null}
      <p className="cnote">{D.taslakDil(WS.destek.dil[s.dil])}</p>
      <p className="cnote">{D.duzenlenebilir}</p>
      <label>
        <span className="cnote" style={{ margin: 0 }}>{D.konuEtiket}</span>
        <input type="text" value={konu} onChange={(e) => setKonu(e.target.value)} data-testid="ws-yanit-konu" style={{ width: '100%' }} />
      </label>
      <label>
        <span className="cnote" style={{ margin: 0 }}>{D.metinEtiket}</span>
        <textarea rows={7} value={metin} onChange={(e) => setMetin(e.target.value)} data-testid="ws-yanit-metin" style={{ width: '100%' }} />
      </label>
      {mesaj ? <p role="status" data-testid="ws-yanit-mesaj">{mesaj}</p> : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Çekmece
// ---------------------------------------------------------------------------

export function Cekmece({ tur, reference, me, git, kapat, degisti }: { tur: WsTur; reference: string; me: Me; git: (h: string) => void; kapat: () => void; degisti?: () => void }) {
  const r = useVeri<WsCekmece>(() => api.webPanelItem(tur, reference), [tur, reference]);
  const [pf, setPf] = useState('hepsi');
  const [not, setNot] = useState('');
  const [mesaj, setMesaj] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const [siparisAcik, setSiparisAcik] = useState(false);
  const kapatDugme = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const onceki = document.activeElement as HTMLElement | null;
    const t = setTimeout(() => kapatDugme.current?.focus(), 30);
    const kacis = (e: KeyboardEvent) => { if (e.key === 'Escape') kapat(); };
    document.addEventListener('keydown', kacis);
    return () => { clearTimeout(t); document.removeEventListener('keydown', kacis); onceki?.focus?.(); };
  }, []);
  const d = r.veri;
  const calis = async (islem: () => Promise<unknown>) => {
    setMesgul(true);
    setMesaj(null);
    try {
      await islem();
      setMesaj(WS.cekmece.yapildi);
      r.yenile();
      degisti?.();
    } catch (e) {
      setMesaj(e instanceof ApiError ? e.message : WS.hata);
    } finally {
      setMesgul(false);
    }
  };
  const yazabilir = (izin: string) => me.permissions.includes(izin);
  return (
    <>
      <div className="scrim on" onClick={kapat} aria-hidden="true" />
      <aside className="drawer open" role="dialog" aria-modal="true" aria-labelledby="drtitle" aria-label={WS.cekmece.aria} data-testid="ws-cekmece">
        <div className="dr-head">
          <div>
            <h3 id="drtitle">{d ? (d.isMember ? d.who : WS.cekmece.ziyaretci(d.who)) : reference}</h3>
            <p>{`${WS.tur[tur]} ${reference}`}</p>
          </div>
          <button type="button" className="x" ref={kapatDugme} aria-label={WS.cekmece.kapat} data-testid="ws-cekmece-kapat" onClick={kapat}>{'\u00d7'}</button>
        </div>
        <div className="dr-body">
          {r.hata && !d ? <Hata mesaj={r.hata} yenile={r.yenile} /> : null}
          {!d && !r.hata ? <p>{WS.yukleniyor}</p> : null}
          {d ? (
            <>
              {d.memberExternalId ? (
                <div style={{ margin: '0 0 var(--space-md)' }}>
                  <button type="button" className="btn sm primary" data-testid="ws-cekmece-kisi" onClick={() => git(`person:${d.memberExternalId}`)}>{WS.cekmece.kisiSayfasi}</button>
                </div>
              ) : null}
              {tur !== 'sip' ? <dl className="facts">
                {d.facts.map(([e, v]) => (
                  <div key={e}><dt>{e}</dt><dd>{v}</dd></div>
                ))}
              </dl> : null}
              {tur === 'sip' ? <div className="workflow-stack" data-testid="ws-cekmece-siparis"><WebOrderDetail me={me} reference={reference} /></div> : null}
              {d.support ? <DestekKaynagi s={d.support} git={git} siparisiAc={() => setSiparisAcik((a) => !a)} /> : null}
              {tur === 'on' && d.onTalep ? <OnTalepIletisim o={d.onTalep} /> : null}
              {d.support && !d.support.system ? <DestekYaniti s={d.support} who={d.who} reference={reference} /> : null}
              <div className="dr-sec"><h4>{WS.cekmece.zaman}</h4></div>
              <ZamanCizelgesi olaylar={d.events} secili={pf} setSecili={setPf} />
              {tur !== 'sip' ? <div className="dr-sec"><h4>{WS.cekmece.karar}</h4></div> : null}
              <div className="decide">
                {tur === 'destek' && d.support && !d.support.system && yazabilir('fulfilment:write') ? (
                  <div className="row">
                    {d.support.sinif !== 'SPAM' ? (
                      <button type="button" className="btn" disabled={mesgul} data-testid="ws-cekmece-spam-kapat" onClick={() => void calis(() => api.markWebSupportSpam(reference, true))}>{WS.destek.spamKapat}</button>
                    ) : (
                      <button type="button" className="btn primary" disabled={mesgul} data-testid="ws-cekmece-spam-degil" onClick={() => void calis(() => api.markWebSupportSpam(reference, false))}>{WS.destek.spamDegil}</button>
                    )}
                  </div>
                ) : null}
                {tur === 'destek' && d.state && d.state !== 'CLOSED' ? (
                  yazabilir('fulfilment:write') ? (
                    <div className="row">
                      {d.state === 'OPEN' ? <button type="button" className="btn primary" disabled={mesgul} data-testid="ws-cekmece-cevaplandi" onClick={() => void calis(() => api.changeWebSupportCaseState(reference, 'ANSWERED'))}>{WS.cekmece.cevaplandi}</button> : null}
                      <button type="button" className="btn" disabled={mesgul} data-testid="ws-cekmece-kapat-is" onClick={() => void calis(() => api.changeWebSupportCaseState(reference, 'CLOSED'))}>{WS.cekmece.kapatDugme}</button>
                    </div>
                  ) : <p>{WS.cekmece.yetkiYok}</p>
                ) : null}
                {tur === 'on' && d.owner !== 'kapali' ? (
                  yazabilir('sales:write') ? (
                    <>
                      <label><span className="cnote" style={{ margin: 0 }}>{WS.kisi.notEtiket}</span><input type="text" value={not} data-testid="ws-cekmece-not" onChange={(e) => setNot(e.target.value)} style={{ width: '100%' }} /></label>
                      <div className="row">
                        <button type="button" className="btn primary" disabled={mesgul || !not.trim()} data-testid="ws-cekmece-ulasildi" onClick={() => void calis(() => api.workWebDemand(reference, { followUpState: 'CONTACTED', note: not.trim() }))}>{WS.cekmece.ulasildi}</button>
                        <button type="button" className="btn" disabled={mesgul || !not.trim()} onClick={() => void calis(() => api.workWebDemand(reference, { followUpState: 'CLOSED', note: not.trim() }))}>{WS.cekmece.kapatDugme}</button>
                      </div>
                    </>
                  ) : <p>{WS.cekmece.yetkiYok}</p>
                ) : null}
                {tur === 'vize' ? <div className="row"><button type="button" className="btn" onClick={() => git('yol:/operasyon')}>{WS.cekmece.operasyonda}</button></div> : null}
                {mesaj ? <p role="status" data-testid="ws-cekmece-mesaj">{mesaj}</p> : null}
              </div>
              {tur === 'destek' && d.support?.orderReference && siparisAcik ? <div className="workflow-stack" data-testid="ws-cekmece-siparis"><WebOrderDetail me={me} reference={d.support.orderReference} onClose={() => setSiparisAcik(false)} /></div> : null}
              <p className="legal">{WS.kisi.hukukMetin}</p>
            </>
          ) : null}
        </div>
      </aside>
    </>
  );
}
